import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLeaflet } from '../../utils/geo';
import { addBaseLayer } from '../../utils/offline/baseLayer';
import { tooltipText } from '../../utils/tooltipText';
import { targetIcon, youIcon } from '../map/MapCanvas';

/*
 * A live event's map (docs/GAME_DESIGN.md Part III section 19): me, my
 * teammates (each in their team's colour), my next waypoint with its arrival
 * circle, and the search ring around it. Small and still in a corner of the
 * camera (`full` false: one tap opens the event's own screen); full-screen
 * and movable on that screen.
 *
 * Everything drawn comes from the heartbeat (EventLiveProvider): teammates'
 * positions are the server's, shown only to their team.
 */
const SEARCH_RING = { color: '#ffb300', weight: 2, dashArray: '6 6', fill: false };
const ARRIVAL = { color: '#d81b60', weight: 2, fillOpacity: 0.08 };
const ACCURACY = { color: '#1565c0', weight: 1, fillOpacity: 0.12 };
const ZONE = { color: '#e65100', weight: 2, fillOpacity: 0.12 };

export default function FieldMiniMap({ fix, positions = [], waypoint = null, teams = [], zones = [], full = false,
  loader = loadLeaflet, onExpand, testid = 'event-minimap' }) {
  const { t } = useTranslation('events');
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const layersRef = useRef([]);
  const fitted = useRef(false);
  const [mapState, setMapState] = useState('loading');
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let live = true;
    setMapState('loading');
    fitted.current = false;
    Promise.resolve().then(() => loaderRef.current()).then((L) => {
      if (!live) return;
      if (!L || typeof L.map !== 'function' || !boxRef.current) throw new Error('no leaflet');
      const map = L.map(boxRef.current, {
        zoomControl: full, attributionControl: full, dragging: full, touchZoom: full, scrollWheelZoom: full,
        doubleClickZoom: full, boxZoom: false, keyboard: full, tap: false, minZoom: 8,
      });
      map.attributionControl?.setPrefix?.(false);
      addBaseLayer(L, map, { attribution: '© OpenStreetMap', maxZoom: 19 });
      mapRef.current = map;
      leafletRef.current = L;
      setMapState('ready');
    }).catch(() => { if (live) setMapState('failed'); });
    return () => {
      live = false;
      mapRef.current?.remove();
      mapRef.current = null;
      layersRef.current = [];
    };
  }, [full]);

  const colourOf = (teamId) => teams.find((x) => x.team_id === teamId)?.colour || '#6d4c41';
  const key = JSON.stringify([fix?.lat, fix?.lng, fix?.accuracy_m, waypoint?.waypoint_id,
    positions.map((p) => [p.user_id, p.lat, p.lng]), zones.map((z) => z.zone_id)]);
  useEffect(() => {
    const L = leafletRef.current;
    const map = mapRef.current;
    if (mapState !== 'ready' || !L || !map) return;
    layersRef.current.forEach((layer) => layer.remove());
    const layers = [];
    const points = [];
    // Fire-safe zones (field isolation, PR11): where a flame may be lit.
    if (typeof L.geoJSON === 'function') {
      zones.forEach((z) => layers.push(L.geoJSON(z.geometry, { style: ZONE, interactive: false }).addTo(map)));
    }
    if (waypoint) {
      layers.push(L.marker([waypoint.lat, waypoint.lng], { icon: targetIcon(L), interactive: false }).addTo(map));
      layers.push(L.circle([waypoint.lat, waypoint.lng], { radius: waypoint.radius_m || 15, ...ARRIVAL }).addTo(map));
      if (waypoint.search_radius_m) {
        layers.push(L.circle([waypoint.lat, waypoint.lng], { radius: waypoint.search_radius_m, ...SEARCH_RING })
          .addTo(map));
      }
      points.push([waypoint.lat, waypoint.lng]);
    }
    positions.forEach((p) => {
      const marker = L.circleMarker([p.lat, p.lng], { radius: 7, color: '#fff', weight: 2, fillOpacity: 1,
        fillColor: colourOf(p.team_id) });
      // A name is typed by its player (a guest too): text, never markup.
      if (full && p.name) marker.bindTooltip?.(tooltipText(p.name));
      layers.push(marker.addTo(map));
      points.push([p.lat, p.lng]);
    });
    if (fix?.lat != null) {
      if (fix.accuracy_m) layers.push(L.circle([fix.lat, fix.lng], { radius: fix.accuracy_m, ...ACCURACY }).addTo(map));
      layers.push(L.marker([fix.lat, fix.lng], { icon: youIcon(L), interactive: false }).addTo(map));
      points.push([fix.lat, fix.lng]);
    }
    layersRef.current = layers;
    // The small map follows everyone; the full one is fitted once, then the
    // person moves it as they like.
    if (points.length && (!full || !fitted.current)) {
      if (points.length === 1) map.setView(points[0], 17);
      else map.fitBounds(L.latLngBounds(points), { padding: [20, 20], maxZoom: 18 });
      fitted.current = true;
    }
  }, [mapState, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const box = (
    <div ref={boxRef} data-testid={`${testid}-leaflet`} style={{ width: '100%', height: '100%' }} />
  );
  if (full) {
    return (
      // Its own stacking context: Leaflet's panes (z-index 400 and up) must
      // never rise over a dialog opened above the page.
      <div data-testid={testid} data-map={mapState} data-full="true"
        style={{ position: 'relative', height: '100%', isolation: 'isolate' }}>
        {box}
        {mapState === 'failed' && <p style={fs.failed} role="status">{t('mapFailed')}</p>}
      </div>
    );
  }
  return (
    <button type="button" data-testid={testid} data-map={mapState} data-full="false" onClick={onExpand}
      aria-label={t('openMap')} title={t('openMap')} style={fs.small}>
      <span style={{ display: 'block', width: '100%', height: '100%', pointerEvents: 'none' }}>{box}</span>
      {mapState === 'failed' && <span style={fs.failedSmall}>{t('mapFailedShort')}</span>}
    </button>
  );
}

const fs = {
  small: {
    position: 'absolute', zIndex: 2, insetInlineStart: 12, top: 'calc(env(safe-area-inset-top) + 110px)',
    width: 132, height: 132, padding: 0, borderRadius: 14, overflow: 'hidden', border: '2px solid rgba(255,255,255,.85)',
    boxShadow: '0 2px 10px rgba(0,0,0,.45)', background: '#2b3a2b', cursor: 'pointer', isolation: 'isolate',
  },
  failed: { position: 'absolute', inset: 'auto 12px 12px', margin: 0, padding: 8, background: '#fff', borderRadius: 8 },
  failedSmall: { position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12 },
};
